import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// BulkDeleteDialog — type-to-confirm archive action.
// =====================================================================

export default function BulkDeleteDialog({ open, onOpenChange, patientIds, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const n = patientIds?.length ?? 0;

  useEffect(() => { if (!open) setConfirmText(""); }, [open]);

  const canConfirm = confirmText === "ARCHIVE" && !submitting;

  async function handleDelete() {
    if (!canConfirm) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("archive_patients", {
      p_ids: patientIds,
      p_reason: "patient manage archive",
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Could not archive patients.");
      return;
    }
    onOpenChange(false);
    onDeleted?.(data);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="bulk-delete-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onOpenChange(false); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
            </div>
            <h2 className="text-base font-bold text-slate-900" data-testid="bulk-delete-title">
              Deactivate/archive {n} {n === 1 ? "patient" : "patients"}?
            </h2>
          </div>
          <button type="button" onClick={() => !submitting && onOpenChange(false)} data-testid="bulk-delete-close" className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            This will hide <span className="font-semibold">{n}</span> {n === 1 ? "patient" : "patients"} from active lists. All charting, medical history, treatment plans, appointments, files, and financial records are kept.
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            Nothing is erased. Archived patients can be reactivated later.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              To confirm, type <code className="px-1 py-0.5 bg-slate-100 rounded text-[12px]">ARCHIVE</code> below
            </label>
            <input
              type="text"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="bulk-delete-confirm-input"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-500"
              placeholder="ARCHIVE"
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 flex items-center justify-end gap-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="bulk-delete-cancel-button"
            className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={!canConfirm}
            data-testid="bulk-delete-submit-button"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Archiving…" : `Archive ${n} ${n === 1 ? "patient" : "patients"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
